import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API from '../lib/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [privacyMode, setPrivacyMode] = useState(() => {
    try { return localStorage.getItem('privacy-mode') === 'true'; } catch { return false; }
  });

  useEffect(() => {
    // On mount, try to hydrate the session using cookies + stored tokens.
    // IMPORTANT: on failure we must NOT setUser(null) — that would race
    // against LoginPage's redirect handler (which stores tokens from a
    // Google OAuth ?auth_token=… URL) and wipe the freshly-set user,
    // bouncing the user back to /login and causing the "dashboard flashes
    // then black screen" bug. Initial state is already null; leaving it
    // untouched is the correct behaviour on failure.
    API.get('/auth/me')
      .then(r => setUser(r.data))
      .catch(() => { /* no-op — do not wipe user on initial hydration failure */ })
      .finally(() => setLoading(false));
  }, []);

  const togglePrivacy = useCallback(() => {
    setPrivacyMode(prev => {
      const next = !prev;
      try { localStorage.setItem('privacy-mode', String(next)); } catch {}
      return next;
    });
  }, []);

  // Keyboard shortcut: Ctrl+Shift+P
  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'P') {
        e.preventDefault();
        togglePrivacy();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [togglePrivacy]);

  const login = async (email, password) => {
    const { data } = await API.post('/auth/login', { email, password });
    if (data.access_token) localStorage.setItem('access_token', data.access_token);
    if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
    setUser(data);
    return data;
  };

  const loginWithGoogle = (userData) => {
    if (userData.access_token) localStorage.setItem('access_token', userData.access_token);
    if (userData.refresh_token) localStorage.setItem('refresh_token', userData.refresh_token);
    setUser(userData);
  };

  const logout = async () => {
    try { await API.post('/auth/logout'); } catch {}
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, logout, setUser, privacyMode, togglePrivacy }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
